import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { incidentDetailRoute } from '@/features/shared/routes';
import { createIncident } from '../emsCommands';
import type { CallNotificationItem } from '@/signalr/contracts';
import { callerMarker } from '@/features/shared/map/markers';
import { IncidentListMapComponent, usePsapMapConfiguration } from '@/features/shared/map/IncidentListMapComponent';
import { PopupView } from '@/features/shared/map/PopupView';
import { CallNotificationsDetailTemplate } from './CallNotificationsDetailTemplate';

export type CallNotificationsMapProps = {
  callNotifications?: readonly CallNotificationItem[];
  disableControls?: boolean;
};

export function CallNotificationsMap({ callNotifications, disableControls = false }: CallNotificationsMapProps) {
  const navigate = useNavigate();
  const mapConfiguration = usePsapMapConfiguration();

  const [center] = useState(() => mapConfiguration.defaultPosition);
  const [zoom] = useState(() => mapConfiguration.defaultZoom);

  const [requestedIncidentId] = useState(() => crypto.randomUUID());
  const createIncidentInProgressRef = useRef(false);

  const [callNotification, setCallNotification] = useState<CallNotificationItem | null>(null);

  const effectiveMarkers = useMemo(
    () =>
      (callNotifications ?? [])
        .filter((call) => call.lastPosition != null)
        .map((call) => callerMarker(call.id, [call.lastPosition!.latitude, call.lastPosition!.longitude])),
    [callNotifications],
  );

  const createNewIncident = async (item: CallNotificationItem) => {
    if (createIncidentInProgressRef.current) {
      return;
    }

    createIncidentInProgressRef.current = true;
    try {
      await createIncident(requestedIncidentId, [item.callId]);
      void navigate(incidentDetailRoute(requestedIncidentId));
    } finally {
      createIncidentInProgressRef.current = false;
    }
  };

  const onMarkerClicked = (markerId: string) => {
    setCallNotification(callNotifications?.find((call) => call.id === markerId) ?? null);
  };

  return (
    <IncidentListMapComponent
      center={center}
      zoom={zoom}
      items={effectiveMarkers}
      disableControls={disableControls}
      onMarkerClicked={onMarkerClicked}
      popupContent={
        <PopupView show={callNotification != null} onClose={() => setCallNotification(null)}>
          {callNotification != null && (
            <CallNotificationsDetailTemplate item={callNotification} onCreateIncident={createNewIncident} />
          )}
        </PopupView>
      }
    />
  );
}
